import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, Sprout, Globe, ShieldCheck, Truck, ArrowRight, CheckCircle2,
  Users, Award, Clock, Handshake, PackageCheck, Scale, IndianRupee, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteDialog from "@/components/QuoteDialog";
import { IMAGES, PRODUCTS, CERTIFICATIONS, MARKETS } from "@/lib/site-data";

const heroFeatures = [
  { icon: Leaf, title: "PREMIUM QUALITY", desc: "Handpicked onions of excellent quality" },
  { icon: Sprout, title: "FARM FRESH GOODNESS", desc: "Sourced directly from trusted farms" },
  { icon: Globe, title: "GLOBAL REACH", desc: "Exporting to 30+ countries worldwide" },
  { icon: ShieldCheck, title: "TRUSTED EXPORTER", desc: "Reliable partner for bulk onion supplies" },
  { icon: Truck, title: "ON TIME DELIVERY", desc: "Timely shipment with safe & secure logistics" },
];

const whyChoose = [
  { icon: Handshake, title: "Carefully Selected & Handpicked" },
  { icon: PackageCheck, title: "Advanced Sorting & Grading" },
  { icon: Scale, title: "Hygienic Packing & Handling" },
  { icon: IndianRupee, title: "Competitive Prices & Best Value" },
  { icon: Globe, title: "Timely Delivery Worldwide" },
];

export default function Home() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Ship banner background */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Cargo ship at port"
            className="w-full h-full object-cover"
            data-testid="hero-banner-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-white" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-sm font-semibold tracking-widest text-slate-500 mb-4" data-testid="hero-eyebrow">PREMIUM QUALITY</p>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              <span className="text-[#0f172a]">FRESH ONIONS</span>
              <br />
              <span className="text-[#1e5ba8]">FROM INDIA</span>
            </h1>
            <p className="mt-6 text-lg font-medium text-slate-700">
              Your Trusted Exporter of Farm Fresh Onions to Global Markets
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl">
              EXIMXPRESS exports premium quality onions from India. We ensure farm freshness, strict quality control, hygienic packing and timely delivery to importers worldwide.
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                "100% Natural & Farm Fresh",
                "Carefully Sourced from Trusted Farms",
                "Strict Quality Control & Grading",
                "Hygienic Packing & Timely Delivery",
              ].map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#1e5ba8]" /> {b}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => setQuoteOpen(true)}
              className="mt-8 bg-[#1e5ba8] hover:bg-[#164582] text-white font-semibold px-6 py-6 text-sm rounded-md shadow-[0_8px_24px_rgba(30,91,168,0.3)]"
              data-testid="hero-request-quote-btn"
            >
              REQUEST A QUOTE <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(15,23,42,0.15)]">
              <img src={IMAGES.onionsHero} alt="Fresh red onions" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1e5ba8]/20 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full bg-white flex flex-col items-center justify-center border-4 border-[#1e5ba8] shadow-xl">
              <div className="text-[10px] font-bold text-[#1e5ba8] text-center leading-tight">100%<br />NATURAL</div>
              <div className="w-8 h-px bg-[#1e5ba8] my-1" />
              <div className="text-[8px] font-semibold text-slate-600 text-center leading-tight">PREMIUM<br />QUALITY</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Row */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="bg-white rounded-xl shadow-[0_10px_40px_rgba(15,23,42,0.06)] border border-slate-100 grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {heroFeatures.map((f) => (
            <div key={f.title} className="p-6 flex flex-col items-center text-center gap-2" data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="w-12 h-12 rounded-md bg-sky-50 flex items-center justify-center text-[#1e5ba8]">
                <f.icon className="w-6 h-6" strokeWidth={2} />
              </div>
              <div className="font-display font-bold text-[13px] tracking-wider text-[#0f172a]">{f.title}</div>
              <div className="text-xs text-slate-500 leading-relaxed max-w-[180px]">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold tracking-widest text-[#1e5ba8] mb-3">ABOUT EXIMXPRESS</p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl leading-tight tracking-tight">
              India&apos;s Reliable Onion<br />Export Partner
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              At EXIMXPRESS, we are committed to exporting the finest quality onions that meet international standards. Our advanced sorting, grading, packing and cold storage facilities ensure that every onion we deliver reaches you fresh, safe and full of goodness.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-5">
              {[
                { icon: ShieldCheck, label: "Quality Assured" },
                { icon: Users, label: "Customer Focused" },
                { icon: Award, label: "Ethical Business" },
                { icon: Clock, label: "Timely Delivery" },
              ].map((i) => (
                <div key={i.label} className="flex flex-col items-start gap-2">
                  <div className="w-10 h-10 rounded-full bg-[#1e5ba8] text-white flex items-center justify-center">
                    <i.icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-semibold text-slate-700">{i.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={IMAGES.onionBasket} alt="Onion basket" className="rounded-2xl w-full h-[420px] object-cover shadow-lg" />
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="bg-slate-50/60 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0f172a]">OUR ONION PRODUCTS</h2>
            <div className="w-16 h-1 bg-[#1e5ba8] mx-auto mt-3 rounded-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] transition-transform duration-300" data-testid={`product-card-${p.id}`}>
                <div className="h-44 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-base tracking-wide">{p.name.toUpperCase()}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.desc}</p>
                  <Link to="/products" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1e5ba8] mt-4 hover:gap-2 transition-all duration-200" data-testid={`product-view-${p.id}`}>
                    View Details <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="bg-sky-50/70 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl text-center text-[#0f172a] mb-10">WHY CHOOSE EXIMXPRESS?</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {whyChoose.map((w) => (
              <div key={w.title} className="flex flex-col items-center text-center gap-3">
                <div className="w-14 h-14 rounded-full bg-white border border-sky-200 flex items-center justify-center text-[#1e5ba8] shadow-sm">
                  <w.icon className="w-6 h-6" />
                </div>
                <div className="text-sm font-semibold text-slate-700 leading-snug">{w.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <p className="text-sm font-semibold tracking-widest text-[#1e5ba8] mb-4">OUR CERTIFICATIONS</p>
            <div className="flex flex-wrap gap-6">
              {CERTIFICATIONS.map((c) => (
                <div key={c.name} className="flex flex-col items-center gap-2 min-w-[100px]" data-testid={`cert-${c.short.toLowerCase()}`}>
                  <div className="w-16 h-16 rounded-full border-2 border-[#1e5ba8] flex items-center justify-center bg-white shadow-sm">
                    <span className="font-display font-bold text-[10px] text-[#1e5ba8] text-center leading-tight px-1">{c.short}</span>
                  </div>
                  <div className="text-xs text-slate-600 text-center leading-tight max-w-[110px]">{c.name}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-sky-50/70 rounded-xl p-6 border border-sky-100">
            <p className="text-sm font-semibold tracking-widest text-[#1e5ba8] mb-2">QUALITY YOU CAN TRUST</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              We follow international quality standards and strict food safety practices to ensure the best quality onions for our global buyers.
            </p>
            <Link to="/quality" className="inline-flex items-center gap-2 mt-4 bg-[#1e5ba8] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#164582]" data-testid="quality-learn-more-btn">
              LEARN MORE <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Global Markets */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-sm font-semibold tracking-widest text-[#1e5ba8] mb-3">GLOBAL MARKETS</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0f172a]">
              Delivering Freshness<br />Across the Globe
            </h2>
            <ul className="mt-6 grid grid-cols-3 gap-y-3 gap-x-4 max-w-md">
              {MARKETS.map((m) => (
                <li key={m} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-[#1e5ba8]" /> {m}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={IMAGES.truck} alt="Logistics truck" className="rounded-2xl w-full h-[280px] object-cover shadow-lg" />
            <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-md text-xs font-semibold text-[#1e5ba8] flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> 30+ Countries
            </div>
          </div>
        </div>
      </section>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
